import apiSauce from "apisauce";
import axiosRetry from "axios-retry";

import { actions } from "@app/utils";
import { createApiClient } from "@app/utils/apiClient";

const S3_UPLOAD_TIMEOUT_MS = 300000; // 5 minutes - fetch from URL + upload to S3 can be slow

export const uploadClient = createApiClient(
  actions["exd-storyteller-ai/uploadFileToS3"],
  null,
  3,
  undefined,
  S3_UPLOAD_TIMEOUT_MS,
);

const awsClient = apiSauce.create({});

/**
 * Retries failed requests up to 3 times with exponential backoff.
 * It retries on network errors or idempotent request errors.
 */
axiosRetry(awsClient.axiosInstance, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
});

/**
 * Uploads a file to a specified URL.
 *
 * @param {string} filePath - The path of the file to upload.
 * @param {string} fileURL - The URL to upload the file to.
 *
 * @returns {Promise<Response>} - A promise that resolves to the response of the upload request.
 */
export const uploadFileByURL = async (filePath, fileURL, options = {}) => {
  const response = await uploadClient.post("", { filePath, fileURL }, options);

  // Treat as success if we have valid response data (backend may have succeeded but client got network/timeout)
  if (response.data?.presignURL != null || response.data?.filePath != null) {
    return response.data;
  }

  if (!response.ok) {
    const reason =
      response.problem ??
      (response.status != null
        ? String(response.status)
        : "connection or timeout");
    throw new Error(`Upload failed: ${reason}`);
  }

  return response.data;
};

/**
 * Uploads a file by creating a writable URL and then uploading the file as a blob.
 *
 * @param {string} filePath - The path of the file to upload.
 * @param {Blob} fileBlob - The Blob object representing the file to upload.
 *
 * @returns {Promise<Object>} - A promise that resolves to the response of the upload request.
 * @throws {Error} - If the upload process fails at any step.
 */
export const uploadFileByBlob = async (filePath, fileBlob, options = {}) => {
  const writableURLresponse = await uploadClient.post(
    "",
    {
      filePath,
      contentType: fileBlob.type || "application/octet-stream",
      writable: true,
    },
    options,
  );

  if (!writableURLresponse.ok) {
    throw new Error(
      `Failed to get writable URL: ${writableURLresponse.status}`,
      { cause: writableURLresponse },
    );
  }

  const {
    presignURL,
    readPresignURL,
    filePath: resolvedFilePath,
  } = writableURLresponse.data;

  // Upload the blob to the writable URL
  const uploadResponse = await awsClient.put(presignURL, fileBlob, {
    headers: {
      Accept: "*/*",
      "Content-Type": fileBlob.type || "application/octet-stream",
    },
    ...options,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Blob upload failed: ${uploadResponse.status}`, {
      cause: uploadResponse,
    });
  }

  // If the backend already provided the read presigned URL, return immediately
  // without making a 3rd HTTP round-trip.
  if (readPresignURL) {
    return {
      filePath: resolvedFilePath ?? filePath,
      presignURL: readPresignURL,
    };
  }

  // Fallback: fetch read presigned URL
  const completeResponse = await uploadClient.post("", {
    filePath: [filePath],
  });

  // console.log("completeResponse", completeResponse);

  if (!completeResponse.ok) {
    throw new Error(`Upload completion failed: ${completeResponse.status}`, {
      cause: completeResponse,
    });
  }

  return completeResponse.data?.urls?.[0];
};

/**
 * Fetches a presigned URL for uploading a file.
 *
 * @param {string} filePath - The path of the file to upload.
 *
 * @returns {Promise<string>} - A promise that resolves to the presigned URL.
 */
export const fetchPresignURL = async (filePath, options = {}) => {
  const response = await uploadClient.post("", { filePath }, options);

  if (!response.ok) {
    throw new Error(`Failed to get presigned URL: ${response.status}`, {
      cause: response,
    });
  }

  return response.data;
};

/**
 * Fetches presigned URLs for uploading multiple files.
 *
 * @param {Array<string>} filePaths - An array of file paths to upload.
 *
 * @returns {Promise<Array<{ presignURL: string, filePath: string }>>} - A promise that resolves to an array of objects containing presigned URLs and their corresponding file paths.
 */
export const batchPresignURLs = async (filePaths) => {
  const response = await uploadClient.post("", {
    filePath: filePaths,
  });

  if (!response.ok) {
    throw new Error(`Failed to get batch presigned URLs: ${response.status}`, {
      cause: response,
    });
  }

  return response.data;
};

/**
 * Lists files in an S3 directory by requesting presigned URLs.
 *
 * @param {string} directoryPath - Directory path ending with "/".
 * @returns {Promise<{ urls: Array<{ filePath: string, presignURL: string }> }>}
 */
export const listFilesInDirectory = async (directoryPath, options = {}) => {
  if (!directoryPath) {
    throw new Error("directoryPath is required");
  }

  const normalizedPath = directoryPath.endsWith("/")
    ? directoryPath
    : `${directoryPath}/`;

  const response = await uploadClient.post(
    "",
    { filePath: normalizedPath },
    options,
  );

  if (!response.ok) {
    throw new Error(`Failed to list directory: ${response.status}`, {
      cause: response,
    });
  }

  return response.data;
};

/**
 * Lists folders in an S3 directory.
 *
 * @param {string} directoryPath - Directory path ending with "/".
 * @returns {Promise<{ folders: Array<string> }>}
 */
export const listFoldersInDirectory = async (directoryPath, options = {}) => {
  if (!directoryPath) {
    throw new Error("directoryPath is required");
  }

  const normalizedPath = directoryPath.endsWith("/")
    ? directoryPath
    : `${directoryPath}/`;

  const response = await uploadClient.post(
    "",
    { filePath: normalizedPath, listFolders: true },
    options,
  );

  if (!response.ok) {
    throw new Error(`Failed to list folders: ${response.status}`, {
      cause: response,
    });
  }

  return response.data;
};
